import { ApolloError, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { DealPipelineInvestmentWorkflowStateFiltersQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { themeVar } from '@/styles/theme';
import { Controller, useForm } from 'react-hook-form';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { EDIT_STRATEGY_PAGE_INVESTMENT_QUERY } from '../modify/queries';
import { DEAL_PIPELINE_INVESTMENTS_QUERY, UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION } from '../queries';

interface UpdateSecurityStatusProps {
  isOpen: boolean;
  investmentId: string;
  workflowStates: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  source: 'dealPipeline' | 'modify';
  openPage?: (path: string, options?: NavigateOptions) => void;
  onClose: () => void;
}

const UpdateSecurityStatus = ({ isOpen, investmentId, workflowStates, source, openPage = () => {}, onClose }: UpdateSecurityStatusProps) => {
  const navigate = useNavigate();
  const { handleSubmit, control } = useForm<{ destinationStateId: string }>({ defaultValues: { destinationStateId: '' } });

  const [listWorkflowStates, setListWorkflowStates] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateInvestmentSecurityStatusTransition, { loading: updateInvestmentSecurityStatusTransitionLoading }] = useMutation(
    UPDATE_INVESTMENT_SECURITY_STATUS_TRANSITION_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [DEAL_PIPELINE_INVESTMENTS_QUERY, EDIT_STRATEGY_PAGE_INVESTMENT_QUERY]
    }
  );

  useEffect(() => {
    if (!workflowStates) return;
    setListWorkflowStates(workflowStates.filter((state) => state.name !== 'terminated').map((state) => ({ label: state.label, value: state.id })));
  }, [workflowStates]);

  const handleUpdateSecurityStatus = async (data: { destinationStateId: string }) => {
    setErrorMessage(null);
    const { destinationStateId } = data;

    await updateInvestmentSecurityStatusTransition({
      variables: {
        data: {
          investmentId,
          rawInput: { destinationStateId }
        }
      },
      onCompleted: () => {
        onClose();
      },
      onError: (err) => {
        const error = err as ApolloError;

        if (error.message.includes('required field errors')) {
          if (source === 'dealPipeline') {
            openPage(`/dealPipeline/modify?backToTitle=Manager Pipeline&investment=${encodeURIComponent(investmentId)}&modalPageType=basicInfo`, {
              state: {
                updateSecurityStatus: true,
                investmentId,
                rawInput: { destinationStateId },
                status: error.message.split(' ')[0],
                source
              }
            });
            return;
          }
          if (source === 'modify') {
            navigate(`/dealPipeline/modify?backToTitle=Manager Pipeline&investment=${encodeURIComponent(investmentId)}&modalPageType=basicInfo`, {
              replace: true,
              state: {
                updateSecurityStatus: true,
                investmentId,
                rawInput: { destinationStateId },
                status: error.message.split(' ')[0],
                source
              }
            });
            onClose();
          }
        }
      }
    });
  };

  if (!investmentId) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Update Status"
      confirmButton={{
        name: 'Update',
        onClick: handleSubmit(handleUpdateSecurityStatus),
        disabled: updateInvestmentSecurityStatusTransitionLoading
      }}
      showRequiredFields={!updateInvestmentSecurityStatusTransitionLoading}
      showCancelButton={!updateInvestmentSecurityStatusTransitionLoading}
      showCloseIcon={!updateInvestmentSecurityStatusTransitionLoading}
      isUseOutsideClick={!updateInvestmentSecurityStatusTransitionLoading}
    >
      {updateInvestmentSecurityStatusTransitionLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalBody>
            <ModalDescription>Transition this strategy to a different step in the workflow.</ModalDescription>
            <Controller
              name="destinationStateId"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Status is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Status"
                  value={value || ''}
                  options={listWorkflowStates}
                  setValue={onChange}
                  errorText={error?.message}
                  isRequiredField
                  styleType={!listWorkflowStates?.length ? 'disabled' : 'default'}
                  size="lg"
                />
              )}
            />
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default UpdateSecurityStatus;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 26px;
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 22.4px;
  text-align: center;
  padding-bottom: 20px;
  color: ${({ theme }) => theme.font.base};
`;
